import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { Loading } from './components/Loading';

// Lazy load all page components
const VSL1 = lazy(() => import('./pages/VSL1'));
const VSL2 = lazy(() => import('./pages/VSL2'));
const Survey1 = lazy(() => import('./pages/Survey1'));
const Survey2 = lazy(() => import('./pages/Survey2'));
const ThankYouVSLPath1 = lazy(() => import('./pages/ThankYouVSLPath1'));
const ThankYouVSLPath2 = lazy(() => import('./pages/ThankYouVSLPath2'));
const NotQualified = lazy(() => import('./pages/NotQualified'));
const Preview = lazy(() => import('./pages/Preview'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Login = lazy(() => import('./pages/Login'));

// Simple auth check - you can replace this with your actual auth logic
const isAuthenticated = () => {
  const authToken = localStorage.getItem('auth_token');
  return !!authToken;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  if (!isAuthenticated()) {
    // Redirect to login if trying to access protected route without auth
    return <Navigate to="/analytics-login" replace />;
  }
  return <>{children}</>;
};

// Root layout component
const RootLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Suspense fallback={<Loading />}>
      {children}
    </Suspense>
  );
};

// Create router with future flags
const routerConfig = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// Create routes with proper nesting
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout><VSL1 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/vsl1',
    element: <RootLayout><VSL1 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/vsl2',
    element: <RootLayout><VSL2 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/survey1',
    element: <RootLayout><Survey1 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/survey2',
    element: <RootLayout><Survey2 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/thank-you-vsl-path1',
    element: <RootLayout><ThankYouVSLPath1 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/thank-you-vsl-path2',
    element: <RootLayout><ThankYouVSLPath2 /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/preview',
    element: <RootLayout><Preview /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/analytics',
    element: <RootLayout><ProtectedRoute><Analytics /></ProtectedRoute></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/analytics-login',
    element: <RootLayout><Login /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/not-qualified',
    element: <RootLayout><NotQualified /></RootLayout>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  }
], routerConfig);

const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
